import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ScrollText } from 'lucide-react';
import {
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Pagination,
  Select,
  Skeleton,
} from '../../components/ui';
import { api, compact } from '../../lib/api';
import { TYPING_DEBOUNCE_MS, useDebounced } from '../../lib/debounce';
import { formatDateTime } from '../../lib/format';
import { useT } from '../../lib/i18n';
import type { Paginated, ReferralEvent, ReferralEventType } from '../../lib/types';
import { CampaignSelect, EVENT_TONES, UserRef } from './shared';

const EVENT_TYPES: ReferralEventType[] = [
  'redeemed',
  'denied',
  'qualified',
  'revoked',
  'reward_granted',
  'reward_failed',
];

/**
 * Everything that happened in the program, newest first. The refusals are
 * the rows worth reading: they leave no trace anywhere else, and a run of them
 * from one account or one address is what farming looks like.
 */
export function ActivityTab() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);

  const filters = {
    campaignId: params.get('campaignId') ?? '',
    type: params.get('type') ?? '',
    deviceId: params.get('deviceId') ?? '',
  };
  // Typed a character at a time; one request per pause, not per keystroke.
  const deviceId = useDebounced(filters.deviceId, TYPING_DEBOUNCE_MS);

  const set = (key: keyof typeof filters, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };

  const query = useMemo(
    () =>
      compact({
        campaignId: filters.campaignId,
        type: filters.type,
        deviceId,
        page,
        limit: 30,
      }),
    [filters.campaignId, filters.type, deviceId, page],
  );

  const events = useQuery({
    queryKey: ['referrals', 'events', query],
    queryFn: () => api.get<Paginated<ReferralEvent>>('/api/referrals/events', query),
  });

  return (
    <div className="space-y-4">
      <div className="grid gap-3 rounded-xl border border-line bg-surface p-3.5 sm:grid-cols-3 sm:p-4">
        <Field label={t('referrals.filter.type')}>
          <Select value={filters.type} onChange={(event) => set('type', event.target.value)}>
            <option value="">{t('referrals.filter.anyType')}</option>
            {EVENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`referrals.event.${type}`)}
              </option>
            ))}
          </Select>
        </Field>
        <CampaignSelect value={filters.campaignId} onChange={(value) => set('campaignId', value)} />
        <Field label={t('referrals.filter.account')}>
          <Input
            className="font-mono text-xs"
            value={filters.deviceId}
            onChange={(event) => set('deviceId', event.target.value.trim())}
            placeholder={t('common.pasteDeviceId')}
          />
        </Field>
      </div>

      {events.isError && <ErrorNote error={events.error} />}

      <Card>
        {events.isPending ? (
          <Skeleton className="h-96" />
        ) : (events.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            icon={<ScrollText size={28} />}
            title={t('referrals.activity.emptyTitle')}
            description={t('referrals.activity.emptyDescription')}
          />
        ) : (
          <>
            <ul className="divide-y divide-line">
              {events.data?.items.map((event) => (
                <EventRow key={event.id} event={event} />
              ))}
            </ul>
            <Pagination
              page={events.data?.page ?? 1}
              totalPages={events.data?.totalPages ?? 1}
              total={events.data?.total ?? 0}
              onChange={setPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}

function EventRow({ event }: { event: ReferralEvent }) {
  const t = useT();
  const amount = event.metadata?.amount;
  const rewardType = event.metadata?.rewardType;

  return (
    <li className="space-y-1.5 px-4 py-3 sm:px-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={EVENT_TONES[event.type]}>{t(`referrals.event.${event.type}`)}</Badge>
        {event.code && (
          <code className="font-mono text-xs font-semibold tracking-wider text-ink">
            {event.code}
          </code>
        )}
        {event.campaignName && (
          <span className="min-w-0 truncate text-xs text-ink-2">{event.campaignName}</span>
        )}
        <span className="ml-auto text-xs text-ink-3">{formatDateTime(event.occurredAt)}</span>
      </div>

      {event.reason && (
        <p className="text-xs text-warn">{t(`referrals.denial.${event.reason}`)}</p>
      )}
      {typeof amount === 'number' && (
        <p className="text-xs text-ink-2">
          {rewardType === 'free_trial'
            ? t('referrals.reward.trialDays', { count: amount })
            : t('referrals.activity.daylights', { count: amount })}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-2">
        <UserRef id={event.userId} />
        {event.actor && <span>{t('referrals.activity.by', { actor: event.actor })}</span>}
        {event.ipAddress && <span className="font-mono">{event.ipAddress}</span>}
      </div>
    </li>
  );
}
